import config from '@root/src/config';
import { useCurrentUser } from '@features/user/hooks/useAuth';
import { userQueries } from '@entities/User/queries';
import { UserFront } from '@root/src/entities/User/types';
import { MessageFront } from '@root/src/entities/Message/types';
import { useRealtime } from '@root/src/hooks/useRealtime';
import apiEndpoints from '@root/src/services/api/endpoints';
import { QUERY_KEYS } from '@root/src/services/api/queries';
import { PaginatedResponse } from '@root/src/types/common';
import { useMutation, UseMutationResult, useQuery, UseQueryResult, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useEffect, useState, useCallback, useMemo } from 'react';
import { ConversationFront, ConversationWithMessagesFront, MarkReadInput } from './types';
import { isTemporaryConversation } from './utils';
import type {
  ConversationMessageNewEvent,
  ConversationMessageUpdatedEvent,
  ConversationMessageDeletedEvent,
  ConversationReadEvent,
  ConversationTypingUpdateEvent,
} from '@services/websocket/RealtimeManager';

const TYPING_INDICATOR_EXPIRY_MS = 3000;

export interface UseConversationsOptions {
  filters?: {
    name?: string;
  };
}

export const useConversations = (
  options?: UseConversationsOptions,
): UseQueryResult<PaginatedResponse<ConversationFront>, Error> => {
  const { data: currentUser } = useCurrentUser();
  const { isConnected } = useRealtime();
  const { filters } = options ?? {};

  return useQuery({
    queryKey: [QUERY_KEYS.CONVERSATIONS, { filters }],
    queryFn: () =>
      apiEndpoints.conversation.getConversations({
        sort: { field: 'lastMessageTimestamp', order: 'DESC' },
        ...(filters ? { filters } : {}),
      }),
    refetchInterval: isConnected ? false : 30000, // Disable polling when WebSocket connected
    staleTime: 0, // Always stale so background refetches pick up anything WS may have missed
    enabled: !!currentUser,
  });
};

export const useConversation = (conversationId?: string): UseQueryResult<ConversationWithMessagesFront, Error> => {
  const { isConnected } = useRealtime();

  return useQuery({
    queryKey: [QUERY_KEYS.CONVERSATION, { id: conversationId }],
    queryFn: () => apiEndpoints.conversation.getConversation(conversationId as string),
    refetchInterval: isConnected ? false : 30000, // Disable polling if WebSocket is connected, otherwise use reduced polling
    enabled: !!conversationId && !isTemporaryConversation(conversationId),
    refetchOnMount: false, // WebSocket keeps cache fresh, no need to refetch on mount
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });
};

export const useNewChatUser = (): UseQueryResult<UserFront, Error> => {
  const router = useRouter();
  const { data: currentUser } = useCurrentUser();
  const newChatWithUserId = router.query.newChatWith as string;

  return useQuery({
    ...userQueries.detail(newChatWithUserId),
    enabled: !!newChatWithUserId && !!currentUser,
  });
};

export const useConversationWithUser = (): UseQueryResult<PaginatedResponse<ConversationFront>, Error> => {
  const router = useRouter();
  const { data: currentUser } = useCurrentUser();
  const newChatWithUserId = router.query.newChatWith as string;

  return useQuery({
    queryKey: [QUERY_KEYS.CONVERSATIONS, { filters: { userId: newChatWithUserId } }],
    queryFn: () => apiEndpoints.conversation.getConversations({ filters: { userId: newChatWithUserId } }),
    enabled: !!newChatWithUserId && !!currentUser,
  });
};

export const useMarkConversationRead = (): UseMutationResult<ConversationFront, Error, MarkReadInput> => {
  return useMutation<ConversationFront, Error, MarkReadInput>({
    mutationFn: ({ conversationId, to }) => apiEndpoints.conversation.markConversationRead(conversationId, to),
    meta: { skipSuccessToast: true },
    retry: false,
    // Note: Don't invalidate conversations here - the WebSocket conversation:read event handles it
  });
};

// Real-time conversation hooks
export interface UseRealtimeConversationOptions {
  conversationId?: string;
  enabled?: boolean;
}

export const useRealtimeConversation = (options: UseRealtimeConversationOptions) => {
  const { conversationId, enabled = true } = options;

  // Always call hooks in the same order
  const { joinRoom, leaveRoom, on, startTyping, stopTyping } = useRealtime();
  const queryClient = useQueryClient();
  const { data: currentUser } = useCurrentUser();
  const [typingUsers, setTypingUsers] = useState<Map<string, { userId: string; timestamp: number }>>(new Map());

  const websocketEnabled = config.features.websocket;

  // Join/leave conversation room
  useEffect(() => {
    if (!websocketEnabled || !conversationId || !enabled || !currentUser) return;

    joinRoom('conversation', conversationId);

    return () => {
      leaveRoom('conversation', conversationId);
    };
  }, [conversationId, enabled, currentUser, joinRoom, leaveRoom, websocketEnabled]);

  // Clean up old typing indicators
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setTypingUsers(prev => {
        const newMap = new Map(prev);
        const keysToDelete: string[] = [];
        newMap.forEach((data, userId) => {
          if (now - data.timestamp > TYPING_INDICATOR_EXPIRY_MS) {
            keysToDelete.push(userId);
          }
        });
        keysToDelete.forEach(key => newMap.delete(key));
        return newMap;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Set up real-time event listeners
  useEffect(() => {
    if (!websocketEnabled || !conversationId || !enabled || !currentUser) return;
    const cleanupFunctions: (() => void)[] = [];

    // New message event
    cleanupFunctions.push(
      on<ConversationMessageNewEvent>('conversation:message:new', (data: ConversationMessageNewEvent) => {
        if (data.conversationId === conversationId) {
          // Update the conversation query data
          queryClient.setQueryData(
            [QUERY_KEYS.CONVERSATION, { id: conversationId }],
            (oldData: ConversationWithMessagesFront | undefined) => {
              if (!oldData) return oldData;

              // Check if the real message already exists (prevent duplicates from refetch)
              const realMessageExists = oldData.messages.some(msg => msg.id === data.message.id);
              if (realMessageExists) {
                return oldData;
              }

              // Check if there's an optimistic message to replace
              const optimisticIndex = oldData.messages.findIndex(
                msg =>
                  msg.id.startsWith('temp-') &&
                  msg.author === data.message.author.id &&
                  msg.content === data.message.content,
              );

              const newMessage: MessageFront = {
                id: data.message.id,
                author: data.message.author.id,
                content: data.message.content,
                createdAt: new Date(data.message.createdAt),
                updatedAt: new Date(data.message.createdAt),
                conversation: conversationId,
                deleted: false,
                readBy: {},
                edited: data.message.edited,
              };

              if (optimisticIndex >= 0) {
                // Update optimistic message with server data but keep the temp ID
                // to prevent React key change which would replay the fade-in animation
                const updatedMessages = [...oldData.messages];
                updatedMessages[optimisticIndex] = {
                  ...updatedMessages[optimisticIndex],
                  content: data.message.content,
                  createdAt: new Date(data.message.createdAt),
                  updatedAt: new Date(data.message.createdAt),
                  edited: data.message.edited,
                };
                return {
                  ...oldData,
                  messages: updatedMessages,
                };
              }

              // Add new message (for messages from other users)
              return {
                ...oldData,
                messages: [...oldData.messages, newMessage],
              };
            },
          );
        }
      }),
    );

    // Message updated event
    cleanupFunctions.push(
      on<ConversationMessageUpdatedEvent>('conversation:message:updated', (data: ConversationMessageUpdatedEvent) => {
        if (data.conversationId === conversationId) {
          queryClient.setQueryData(
            [QUERY_KEYS.CONVERSATION, { id: conversationId }],
            (oldData: ConversationWithMessagesFront | undefined) => {
              if (!oldData) return oldData;

              return {
                ...oldData,
                messages: oldData.messages.map(msg =>
                  msg.id === data.message.id
                    ? {
                        ...msg,
                        content: data.message.content,
                        updatedAt: new Date(data.message.updatedAt),
                        edited: data.message.edited,
                      }
                    : msg,
                ),
              };
            },
          );
        }
      }),
    );

    // Message deleted event
    cleanupFunctions.push(
      on<ConversationMessageDeletedEvent>('conversation:message:deleted', (data: ConversationMessageDeletedEvent) => {
        if (data.conversationId === conversationId) {
          queryClient.setQueryData(
            [QUERY_KEYS.CONVERSATION, { id: conversationId }],
            (oldData: ConversationWithMessagesFront | undefined) => {
              if (!oldData) return oldData;

              return {
                ...oldData,
                messages: oldData.messages.map(msg =>
                  msg.id === data.messageId ? { ...msg, deleted: true, content: '[Message deleted]' } : msg,
                ),
              };
            },
          );
        }
      }),
    );

    // Read receipt event
    cleanupFunctions.push(
      on<ConversationReadEvent>('conversation:read', (data: ConversationReadEvent) => {
        if (data.conversationId === conversationId) {
          queryClient.setQueryData(
            [QUERY_KEYS.CONVERSATION, { id: conversationId }],
            (oldData: ConversationWithMessagesFront | undefined) => {
              if (!oldData) return oldData;

              return {
                ...oldData,
                messages: oldData.messages.map(msg => ({
                  ...msg,
                  readBy: {
                    ...msg.readBy,
                    [data.userId]: new Date(data.readAt),
                  },
                })),
              };
            },
          );
        }
      }),
    );

    // Typing update event
    cleanupFunctions.push(
      on<ConversationTypingUpdateEvent>('conversation:typing:update', (data: ConversationTypingUpdateEvent) => {
        if (data.conversationId === conversationId && data.userId !== currentUser?.id) {
          setTypingUsers(prev => {
            const newMap = new Map(prev);
            if (data.isTyping) {
              newMap.set(data.userId, {
                userId: data.userId,
                timestamp: Date.now(),
              });
            } else {
              newMap.delete(data.userId);
            }
            return newMap;
          });
        }
      }),
    );

    return () => {
      cleanupFunctions.forEach(cleanup => cleanup());
    };
  }, [conversationId, enabled, currentUser, on, websocketEnabled, queryClient]);

  // Return stable functions
  const startTypingCallback = useCallback(() => {
    if (conversationId) startTyping(conversationId);
  }, [startTyping, conversationId]);

  const stopTypingCallback = useCallback(() => {
    if (conversationId) stopTyping(conversationId);
  }, [stopTyping, conversationId]);

  const typingUsersArray = useMemo(() => Array.from(typingUsers.values()), [typingUsers]);

  return {
    startTyping: startTypingCallback,
    stopTyping: stopTypingCallback,
    typingUsers: typingUsersArray,
  };
};
